import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import API from "../lib/api";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [cartOpen, setCartOpen] = useState(false);
  const [cartLoading, setCartLoading] = useState(true);
  const [wishlist, setWishlist] = useState([]);

  const fetchCart = useCallback(async () => {
    if (!user || user === false) {
      setCart({ items: [], total: 0 });
      if (user === false) setCartLoading(false);
      return;
    }
    setCartLoading(true);
    try {
      const { data } = await API.get("/cart");
      setCart(data && typeof data === "object" && Array.isArray(data.items) ? data : { items: [], total: 0 });
    } catch (err) {
      console.error("Failed to fetch cart:", err);
      setCart({ items: [], total: 0 });
    }
    setCartLoading(false);
  }, [user]);

  const fetchWishlist = useCallback(async () => {
    if (!user || user === false) { setWishlist([]); return; }
    try {
      const { data } = await API.get("/wishlist");
      setWishlist(data && Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      console.error("Failed to fetch wishlist:", err);
      setWishlist([]);
    }
  }, [user]);

  useEffect(() => { fetchCart(); fetchWishlist(); }, [fetchCart, fetchWishlist]);

  const addToCart = useCallback(async (productId, quantity = 1) => {
    await API.post("/cart", { product_id: productId, quantity });
    await fetchCart();
    setCartOpen(true);
  }, [fetchCart]);

  const updateCartItem = useCallback(async (productId, quantity) => {
    await API.put(`/cart/${productId}`, { product_id: productId, quantity });
    await fetchCart();
  }, [fetchCart]);

  const removeFromCart = useCallback(async (productId) => {
    await API.delete(`/cart/${productId}`);
    await fetchCart();
  }, [fetchCart]);

  const toggleWishlist = useCallback(async (productId) => {
    const { data } = await API.post(`/wishlist/${productId}`);
    await fetchWishlist();
    return data.added;
  }, [fetchWishlist]);

  const isInWishlist = useCallback(
    (productId) => (Array.isArray(wishlist) ? wishlist : []).some(item => item.id === productId),
    [wishlist]
  );

  const safeItems = cart && Array.isArray(cart.items) ? cart.items : [];
  const cartCount = safeItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

  const value = useMemo(() => ({
    cart, cartOpen, setCartOpen, cartCount, cartLoading, wishlist,
    addToCart, updateCartItem, removeFromCart,
    toggleWishlist, isInWishlist, fetchCart, fetchWishlist
  }), [cart, cartOpen, cartCount, cartLoading, wishlist, addToCart, updateCartItem, removeFromCart, toggleWishlist, isInWishlist, fetchCart, fetchWishlist]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
